import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO_MARK } from "@/lib/brand";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";

const PLAN_PRICE = { growth: 29, scale: 79 };

const firePurchaseOnce = (sessionId, plan) => {
  const value = PLAN_PRICE[plan];
  if (!value) return; // only paid plans (Growth/Scale) are conversions
  const key = `bs_purchase_fired:${sessionId}`;
  try {
    if (localStorage.getItem(key)) return; // guard against double-counting on refresh
    localStorage.setItem(key, "1");
  } catch (e) {}
  track("purchase", {
    transaction_id: sessionId,
    value,
    currency: "GBP",
    items: [{ item_id: plan, item_name: plan === "scale" ? "Scale" : "Growth", price: value, quantity: 1 }],
  });
};

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | error | timeout
  const [plan, setPlan] = useState("");
  const [interval, setIntervalState] = useState("monthly");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      if (attempts.current >= 6) { if (active) setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (!active) return;
        setPlan(data.plan);
        if (data.interval) setIntervalState(data.interval);
        if (data.paid || data.payment_status === "paid" || data.payment_status === "no_payment_required") { setState("success"); firePurchaseOnce(sessionId, data.plan); await refreshMe(); return; }
        if (data.status === "expired") { setState("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (active) setState("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [params, refreshMe]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-10 max-w-md w-full text-center" data-testid="checkout-status">
        <img src={LOGO_MARK} alt="TradeOff" className="h-12 w-12 object-contain mx-auto" />
        {state === "checking" && (
          <>
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin mx-auto mt-6" />
            <h1 className="font-display text-xl font-bold text-slate-900 mt-4">Confirming your payment…</h1>
            <p className="text-slate-500 mt-2 text-sm">Hang tight while Stripe confirms your subscription.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mt-6" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">You're on {plan === "scale" ? "Scale" : "Growth"}! 🎉</h1>
            <p className="text-slate-500 mt-2 text-sm">Your new active-project capacity is live. Your plan renews {interval === "annual" ? "annually (2 months free)" : "monthly"} — cancel anytime from Billing.</p>
            <Button onClick={() => navigate("/app")} data-testid="checkout-continue" className="mt-6 w-full bg-blue-600 hover:bg-blue-700">Go to projects</Button>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle className="h-12 w-12 text-amber-500 mx-auto mt-6" />
            <h1 className="font-display text-xl font-bold text-slate-900 mt-4">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="text-slate-500 mt-2 text-sm">{state === "timeout" ? "Your payment may take a moment. Check your billing page shortly." : "We couldn't confirm the payment. You can try again from the billing page."}</p>
            <Button onClick={() => navigate("/app/account")} className="mt-6 w-full bg-slate-900 hover:bg-slate-800">Go to billing</Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
